"use client";
import { Reserve } from "@/features/reserve/components/Reserve/Reserve";
import { ReserveOptions } from "@/features/reserve/components/Reserve/types";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { cn } from "@gettactic/helpers/src/css/cn";
import { scheduleResourceStyling } from "@/lib/utils";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import Image from "next/image";
import { ReserveFormProvider } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { useRoomReservationWeb } from "@gettactic/hooks";
import {
  DelegatedPermissionRequest,
  DelegatedPermissionRequestContent
} from "./DelegatedPermissionRequest";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { delegatedPermissionPartials } from "@gettactic/helpers";
import React, { useCallback } from "react";
import { Modal } from "@/ui/_to_replace/Modal/Modal";

type Props = {
  reserveModal: boolean;
  onClose: () => void;
  wpRequestsRef?: React.RefObject<WorkplaceRequestsRef | null>;
} & ReserveOptions;

export function ReserveModal({
  reserveModal = false,
  resourceType = "desk",
  isEmbedded = false,
  start,
  isAdvancedRecurringOpenByDefault,
  time,
  duration,
  office,
  resource,
  floorId,
  schedule,
  onClose,
  workplaceRequestItems,
  wpRequestsRef,
  attenders,
  title,
  description,
  owner,
  recurring,
  onSuccess,
  onlineMeetingProvider
}: Props) {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const isParking =
    resourceType === "parking_lot" || resourceType === "parking_space";
  const customParkingVerbiage = useCustomParkingVerbiage();
  const hideImage = isParking && customParkingVerbiage !== null;

  const styling = scheduleResourceStyling(
    resourceType,
    customParkingVerbiage !== null
  );

  const {
    requireDelegatedPermission,
    msAuthUrl,
    onRoomChange,
    onOrganizerChange,
    organizerRequiresDelegatedAccess,
    hasMSCalendar,
    hasGoogleCalendar
  } = useRoomReservationWeb({
    isOpen: !!reserveModal,

    isRoomReservation: resourceType === "meeting_room"
  });

  const contentHeader = (
    <div
      className={`${
        styling?.style
      } h-[80px] w-full overflow-hidden rounded-t-lg p-4`}
    >
      <div className="flex h-full items-center justify-between">
        <h4 className="font-bold text-2xl text-tertiary">
          {requireDelegatedPermission ? (
            delegatedPermissionPartials.title
          ) : !schedule ? (
            <>{styling?.title} Reservation</>
          ) : (
            <span>Edit Reservation</span>
          )}
        </h4>
        <div>
          <div className="flex h-full items-center justify-center">
            {hideImage || requireDelegatedPermission ? null : (
              <Image
                src={styling?.image ?? ""}
                width={140}
                height={115}
                alt={styling?.alt ?? ""}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const handleConnectOutlook = useCallback(() => {
    window.open(msAuthUrl);
  }, [msAuthUrl]);

  const content = requireDelegatedPermission ? (
    isMd ? (
      <DelegatedPermissionRequest msAuthUrl={msAuthUrl} onClose={onClose} />
    ) : (
      <DelegatedPermissionRequestContent
        msAuthUrl={msAuthUrl}
        handleConnectOutlook={handleConnectOutlook}
      />
    )
  ) : (
    <>
      {reserveModal ? (
        <div
          style={{
            maxHeight: browserHeight ? `${browserHeight - 200}px` : "32rem"
          }}
          className={cn(
            "v2-reservation max-w-[36rem] md:px-4 font-inter",
            browserHeight && browserHeight < 700 ? "overflow-y-auto" : ""
          )}
        >
          <ReserveFormProvider
            key={`${resource?.id || "new"}-${duration || 0}-${time?.getTime() || 0}`}
            resourceType={resourceType}
            isEmbedded={isEmbedded}
            start={start}
            time={time}
            duration={duration}
            office={office}
            resource={resource}
            floorId={floorId}
            schedule={schedule}
            onSuccess={(scheduleIds) => {
              onSuccess?.(scheduleIds);
              onClose?.();
            }}
            onRoomChange={onRoomChange}
            onOrganizerChange={onOrganizerChange}
            organizerRequiresDelegatedAccess={organizerRequiresDelegatedAccess}
            hasMSCalendar={hasMSCalendar}
            hasGoogleCalendar={hasGoogleCalendar}
            wpRequestsRef={wpRequestsRef}
            workplaceRequestItems={workplaceRequestItems}
            attenders={attenders}
            title={title}
            owner={owner}
            isAdvancedRecurringOpenByDefault={isAdvancedRecurringOpenByDefault}
            recurring={recurring}
            description={description}
            onlineMeetingProvider={onlineMeetingProvider}
          >
            <div className={"px-4"}>
              <Reserve onClose={onClose} />
            </div>
          </ReserveFormProvider>
        </div>
      ) : null}
    </>
  );

  if (!isMd) {
    return (
      <MobileDrawer
        isOpen={reserveModal}
        size={"fit"}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onClose?.();
          }
        }}
        contentUnderGrabber
        scrollableContent
        className="v2-reservation"
      >
        {contentHeader}
        <div className={"pt-2"} />
        {content}
        <div className={"pt-6"} />
      </MobileDrawer>
    );
  }

  return (
    <Modal
      isOpen={reserveModal}
      onClose={onClose}
      title={requireDelegatedPermission ? undefined : contentHeader}
      closeOnOverlayClick
      className={"max-w-[36rem] p-0"}
      contentClassName={"max-w-[36rem]"}
    >
      {content}
    </Modal>
  );
}
